import { resolve, dirname, extname } from 'node:path';
import { readFile, copyFile } from 'node:fs/promises';
import { render } from 'ejs';
import _fm from 'front-matter';
import { outputDir, outputFile } from '@wix/cli-fs';
import type { GeneratorOptions, TemplateAttributes } from './types.js';
import { getFiles } from './fs-extra.js';

// @ts-expect-error - CJS/ESM typescript issue
const fm: typeof _fm.default = _fm;

function shouldSkipParsing(file: string) {
  // We could use some library for checking if file is binary,
  // but currently until we generate more binary files, it's good enough.
  // https://wix.atlassian.net/browse/FEDINF-13501
  return ['.png', '.jpg', '.gif', '.astro'].includes(
    extname(file).toLowerCase()
  );
}

async function justCopy({
  cwd,
  template,
  path,
}: {
  cwd: string;
  template: string;
  path: string;
}) {
  const to = resolve(cwd, template);
  await outputDir(dirname(to));
  await copyFile(path, to);
  return to;
}

export async function generator(opts: GeneratorOptions) {
  const { template: templateRoot, data, cwd } = opts;
  const templates = await getFiles(templateRoot);

  const generatedFiles = await Promise.all(
    templates.map(async (template) => {
      const path = resolve(templateRoot, template);

      if (shouldSkipParsing(path)) {
        // Templates not always contain textual files.
        // Some files can be binary, ex. png. In such cases we just need to copy them.
        return justCopy({ cwd, template, path });
      }

      const raw = await readFile(path, 'utf8');
      const content = render(raw, data);

      const { attributes, body } = fm<TemplateAttributes>(content);

      if (attributes.skip_if === true) {
        return;
      }

      const file = resolve(cwd, attributes.to ?? template);

      await outputFile(file, body);

      return file;
    })
  );

  return generatedFiles.filter((file) => file !== undefined);
}
